'use client'

import * as React from 'react'
import { Building2, Mail, Users } from 'lucide-react'
import { addDoc, collection, getDocs, query, serverTimestamp, where } from 'firebase/firestore'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { toast } from 'sonner'
import { auth, db } from '../../../../firebase'
import { useProject } from './project-context'
import { useUser } from 'components/context/user-context'

const typeColors = {
  university: 'bg-blue-100 text-blue-800',
  ngo: 'bg-emerald-100 text-emerald-800',
  government: 'bg-indigo-100 text-indigo-800',
  industry: 'bg-amber-100 text-amber-800',
  community: 'bg-rose-100 text-rose-800',
}
const AUDIT_COLLECTION = 'auditLogs'

export default function ProjectPartnersPage() {
  const { project } = useProject()
  const { canEdit } = useUser()
  const [partners, setPartners] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [open, setOpen] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [form, setForm] = React.useState({
    orgName: '',
    type: 'ngo',
    contactPerson: '',
    contactEmail: '',
    role: '',
  })

  React.useEffect(() => {
    let isMounted = true

    async function loadPartners() {
      try {
        const partnersQuery = query(collection(db, 'partners'), where('projectId', '==', project.id))
        const snapshot = await getDocs(partnersQuery)
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setPartners(records)
      } catch {
        setPartners([])
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    loadPartners()

    return () => {
      isMounted = false
    }
  }, [project.id])

  const handleSavePartner = async () => {
    if (!form.orgName.trim()) {
      toast.error('Please enter the organization name.')
      return
    }
    if (saving) return
    setSaving(true)
    try {
      const payload = {
        projectId: project.id,
        orgName: form.orgName.trim(),
        type: form.type,
        contactPerson: form.contactPerson.trim(),
        contactEmail: form.contactEmail.trim(),
        role: form.role.trim() || 'Partner',
        documentsShared: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
      const docRef = await addDoc(collection(db, 'partners'), payload)
      setPartners((prev) => [{ id: docRef.id, ...payload }, ...prev])
      try {
        const currentUser = auth.currentUser
        await addDoc(collection(db, AUDIT_COLLECTION), {
          timestamp: serverTimestamp(),
          userId: currentUser?.uid || 'system',
          userDisplayName: currentUser?.displayName || currentUser?.email || 'System',
          action: 'CREATE',
          entity: 'Project',
          entityId: project.id,
          changes: {
            before: '',
            after: `Added project partner "${payload.orgName}"`,
          },
        })
      } catch (error) {
        console.warn('Failed to write audit log', error)
      }
      toast.success('Partner added.')
      setForm({
        orgName: '',
        type: 'ngo',
        contactPerson: '',
        contactEmail: '',
        role: '',
      })
      setOpen(false)
    } catch (error) {
      toast.error('Failed to add partner', {
        description: error?.message || 'Please try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Project Partners</CardTitle>
          {canEdit ? (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm">Add Partner</Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Project Partner</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Organisation Name</Label>
                  <Input
                    value={form.orgName}
                    onChange={(event) => setForm((prev) => ({ ...prev, orgName: event.target.value }))}
                    placeholder="Enter organisation name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Partner Type</Label>
                  <Select value={form.type} onValueChange={(value) => setForm((prev) => ({ ...prev, type: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(typeColors).map((type) => (
                        <SelectItem key={type} value={type}>
                          {type.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Contact Person</Label>
                    <Input
                      value={form.contactPerson}
                      onChange={(event) => setForm((prev) => ({ ...prev, contactPerson: event.target.value }))}
                      placeholder="Full name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={form.contactEmail}
                      onChange={(event) => setForm((prev) => ({ ...prev, contactEmail: event.target.value }))}
                      placeholder="email@example.com"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Input
                    value={form.role}
                    onChange={(event) => setForm((prev) => ({ ...prev, role: event.target.value }))}
                    placeholder="Implementation Partner"
                  />
                </div>
                <Button className="w-full" onClick={handleSavePartner} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Partner'}
                </Button>
              </div>
              </DialogContent>
            </Dialog>
          ) : null}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading partners...</p>
          ) : partners.length === 0 ? (
            <p className="text-sm text-muted-foreground">No partners linked to this project yet.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {partners.map((partner) => (
                <Card key={partner.id} className="hover:shadow-md transition-shadow duration-150">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
                          <Building2 className="size-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground text-sm">{partner.orgName || 'Partner'}</h3>
                          <Badge
                            variant="secondary"
                            className={`text-[10px] mt-0.5 border-0 ${typeColors[partner.type] ?? ''}`}
                          >
                            {(partner.type || 'partner').toUpperCase()}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Users className="size-3.5 shrink-0" />
                        <span>{partner.contactPerson || 'Contact not set'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="size-3.5 shrink-0" />
                        <span className="truncate">{partner.contactEmail || 'No email'}</span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-muted-foreground">Role</p>
                      <p className="text-sm font-medium text-foreground">{partner.role || 'Partner'}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
